/**
 * License Duration Constants and Helpers
 */

import { LicenseDurationType } from '../types/license';

export interface LicenseDuration {
  label: string;
  value: LicenseDurationType;
  days: number | null;
}

// License duration options
export const LICENSE_DURATIONS: LicenseDuration[] = [
  { label: 'Not Licensed', value: 'none', days: null },
  { label: '3 Months', value: '3months', days: 90 },
  { label: '6 Months', value: '6months', days: 180 },
  { label: '12 Months', value: '12months', days: 365 },
  { label: '2 Years', value: '2years', days: 730 },
  { label: '3 Years', value: '3years', days: 1095 },
  { label: '5 Years', value: '5years', days: 1825 },
  { label: 'Blanket', value: 'blanket', days: null },
];

// Selectable durations (excludes 'none' for UI selection)
export const SELECTABLE_DURATIONS = LICENSE_DURATIONS.filter(d => d.value !== 'none');

/**
 * Calculate expiry date from now based on duration type
 * @param durationType - The duration type selected
 * @returns Expiry date as milliseconds since epoch, or null for blanket/none
 */
export const calculateExpiryDate = (durationType: LicenseDurationType): number | null => {
  if (durationType === 'none' || !durationType) {
    return null;
  }

  if (durationType === 'blanket') {
    return null; // Blanket licenses don't expire
  }

  if (durationType === 'custom') {
    return null; // Custom dates are set manually
  }

  const durationItem = LICENSE_DURATIONS.find(item => item.value === durationType);
  if (!durationItem || durationItem.days === null) {
    return null;
  }

  const now = new Date();
  return new Date(now.getTime() + (durationItem.days * 24 * 60 * 60 * 1000)).getTime();
};

/**
 * Calculate expiry date from a specific start date
 * @param startDate - Start date as milliseconds since epoch
 * @param durationType - The duration type selected
 * @returns Expiry date as milliseconds since epoch, or null for blanket/none/custom
 */
export const calculateExpiryDateFromStart = (
  startDate: number,
  durationType: LicenseDurationType
): number | null => {
  if (durationType === 'none' || !durationType) {
    return null;
  }

  if (durationType === 'blanket') {
    return null; // Blanket licenses don't expire
  }

  if (durationType === 'custom') {
    return null; // Custom dates are set manually
  }

  const durationItem = LICENSE_DURATIONS.find(item => item.value === durationType);
  if (!durationItem || durationItem.days === null) {
    return null;
  }

  return startDate + (durationItem.days * 24 * 60 * 60 * 1000);
};

/**
 * Get the label for a duration type
 * @param durationType - The duration type
 * @returns The human-readable label
 */
export const getDurationLabel = (durationType: LicenseDurationType): string => {
  const item = LICENSE_DURATIONS.find(d => d.value === durationType);
  return item?.label || 'Unknown';
};

/**
 * Check if a license is currently active based on expiry date
 * @param dateExpires - Expiry date as milliseconds since epoch (null for blanket)
 * @param durationType - The duration type
 * @returns true if license is active, false if expired
 */
export const isLicenseActive = (
  dateExpires: number | null,
  durationType: LicenseDurationType
): boolean => {
  // Not licensed
  if (durationType === 'none') {
    return false;
  }

  // Blanket licenses are always active
  if (durationType === 'blanket' || dateExpires === null) {
    return true;
  }

  // Check if expired
  return dateExpires > Date.now();
};

/**
 * Get days remaining on a license
 * @param dateExpires - Expiry date as milliseconds since epoch
 * @returns Number of days remaining, or null for blanket/expired
 */
export const getDaysRemaining = (dateExpires: number | null): number | null => {
  if (dateExpires === null) {
    return null; // Blanket license
  }

  const now = Date.now();
  if (dateExpires <= now) {
    return 0; // Expired
  }

  const msRemaining = dateExpires - now;
  return Math.ceil(msRemaining / (24 * 60 * 60 * 1000));
};
